import { useCallback, useMemo } from 'react'
import { useMutation, useQuery } from 'apollo-client'
import { GraphQLError, UserError } from 'helpers'
import { getPlanDifferencePercent } from 'helpers/getters'

import changePlansInquireQuery from './graph/changePlansInquiry.graphql'
import changeSubscriptionPlanQuery from './graph/changeSubscriptionPlan.graphql'
import type { ChangeSubscriptionPlanVariables } from './graph/changeSubscriptionPlan.graphql'


type UseUpgradePlansParams = {
  excludeSelectedPlanFromOptions?: boolean
}

export const useUpgradePlans = ({ excludeSelectedPlanFromOptions }: UseUpgradePlansParams = {}) => {
  const { data, error, isFetching } = useQuery(changePlansInquireQuery, {
    variables: {
      withUpgradePlans: true,
      input: {
        includeCurrent: true,
      },
    },
  })

  const result = useMemo<{
    selectedPlan: SubscriptionModule.UpgradePlan
    productsPerPeriodPlans: SubscriptionModule.UpgradePlan[] // to upgrade only product per month count
    productOptions: SubscriptionModule.UpgradePlan[] // all available
    monthOptionsMap: SubscriptionModule.UpgradePlansByMonth
  }>(() => {
    const plans = data?.currentUser?.data?.subscription?.changePlansInquiry?.upgradePlans

    if (!plans?.length) {
      return {
        selectedPlan: null,
        productsPerPeriodPlans: null,
        productOptions: null,
        monthOptionsMap: null,
      }
    }

    const selectedPlan = { ...plans.find(({ selected }) => selected).plan, selected: true }
    const productsPerPeriodPlans: SubscriptionModule.UpgradePlan[] = []
    const monthOptionsMap: SubscriptionModule.UpgradePlansByMonth = {}

    plans.forEach(({ plan, selected }) => {
      if (excludeSelectedPlanFromOptions && selected) {
        return
      }

      const finalData = {
        ...plan,
        selected,
        percentageDiscount: getPlanDifferencePercent(selectedPlan.productPrice, plan.productPrice),
      }

      // to change products per period count, we return only plans with the same billing & shipping periods
      if (
        plan.billingPeriod === selectedPlan.billingPeriod
        && plan.shippingPeriod === selectedPlan.shippingPeriod
      ) {
        productsPerPeriodPlans.push(finalData)
      }

      // 1. split plans by productsPerPeriod
      if (!monthOptionsMap[plan.productsPerPeriod]) {
        monthOptionsMap[plan.productsPerPeriod] = []
      }

      monthOptionsMap[plan.productsPerPeriod].push(finalData)
    })

    // 2. for every available period get the first plan which is the cheapest
    const productOptions: SubscriptionModule.UpgradePlan[] = Object.keys(monthOptionsMap).sort().map((key) => {
      return monthOptionsMap[key][0]
    })

    return {
      selectedPlan,
      productsPerPeriodPlans,
      productOptions,
      monthOptionsMap,
    }
  }, [ data, excludeSelectedPlanFromOptions ])

  return {
    ...result,
    error,
    isFetching,
  }
}

export const useDowngradePlans = () => {
  const { data, error, isFetching } = useQuery(changePlansInquireQuery, {
    variables: {
      withDowngradePlans: true,
      input: {
        includeCurrent: false,
      },
    },
  })

  const plans = useMemo<SubscriptionModule.UpgradePlan[]>(() => {
    const downgradePlans = data?.currentUser?.data?.subscription?.changePlansInquiry?.downgradePlans

    if (!downgradePlans) {
      return null
    }

    return downgradePlans.map(({ plan }) => ({
      ...plan,
      selected: false,
    }))
  }, [ data ])

  return {
    plans,
    error,
    isFetching,
  }
}

export const useChangeFrequencyPlans = () => {
  const { data, error, isFetching } = useQuery(changePlansInquireQuery, {
    variables: {
      withChangeFrequencyPlans: true,
      input: {
        includeCurrent: false,
      },
    },
  })

  const plans = data?.currentUser?.data?.subscription?.changePlansInquiry?.changeFrequencyPlans

  return {
    plans,
    error,
    isFetching,
  }
}


export const useChangePlan = () => {
  const [ mutate, { isFetching, client } ] = useMutation(changeSubscriptionPlanQuery, {
    fetchPolicy: 'no-cache',
    refetchQueries: [ 'Subscription' ],
    awaitRefetchQueries: true,
  })

  const submit = useCallback(async (input: ChangeSubscriptionPlanVariables['input'], { action }: { action?: 'downgrade' | 'upgrade'} = {}) => {
    const result = await mutate({
      variables: {
        input,
      },
    })

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const { data, error } = result.data.subscriptionChangePlan

    if (error) {
      throw new UserError({ ...error, action }, error.message)
    }

    // reset plans cache
    client.cache.evict({
      id: 'ScentbirdSubscription:{}',
      fieldName: 'changePlansInquiry',
    })

    // queue depends on a plan
    client.cache.evict({ id: 'UserData:{}', fieldName: 'queue' })

    return data
  }, [ mutate, client ])

  return [
    submit,
    {
      isFetching,
      client,
    },
  ] as const
}
